/**
 * ComponenteChatIA - Interface de Chat com Inteligência Artificial
 *
 * CONTEXTO DE NEGÓCIO:
 * Este é o componente principal de interação do usuário com a IA durante
 * a análise de um processo/petição. Permite que o advogado faça perguntas,
 * solicite ajustes na minuta, peça esclarecimentos sobre os pareceres e
 * dê comandos para o agente editor (ex: "adicione um parágrafo sobre X").
 *
 * RESPONSABILIDADES:
 * - Exibir histórico completo da conversa (mensagens do usuário e da IA)
 * - Capturar entrada de texto do usuário
 * - Enviar mensagens para o backend via WebSocket (TAREFA-092)
 * - Receber e exibir respostas da IA em tempo real
 * - Rolagem automática para a última mensagem
 * - Gerenciamento de estado local do chat
 *
 * FUNCIONALIDADES:
 * 1. Campo de input com suporte para Enter (envio) e Shift+Enter (quebra de linha)
 * 2. Botão de envio com feedback visual
 * 3. Área de mensagens com scroll automático
 * 4. Diferenciação visual entre mensagens do usuário e da IA
 * 5. Indicador de "IA está digitando..."
 * 6. Timestamps em cada mensagem
 *
 * INTEGRAÇÃO:
 * - Usa WebSocket (TAREFA-092) para comunicação bidirecional
 * - Integrado na página de resultados (AnalisePeticaoInicial.tsx)
 * - Comunica-se com backend/src/agentes/agente_editor_minuta.py (TAREFA-089)
 *
 * IMPLEMENTAÇÃO:
 * - useState para gerenciar lista de mensagens e estado do input
 * - useEffect para scroll automático ao receber novas mensagens
 * - useRef para manter referência ao container de mensagens
 * - WebSocket connection será implementado na TAREFA-092
 *
 * DESIGN:
 * - Pode ser renderizado como painel lateral (sidebar) ou widget flutuante
 * - Interface limpa e moderna com Tailwind CSS
 * - Responsivo para diferentes tamanhos de tela
 *
 * NOTA PARA LLMs:
 * Este componente faz parte da TAREFA-091 - Frontend Componente de Chat.
 * A integração com WebSocket será feita na TAREFA-092.
 * Por enquanto, este componente gerencia apenas o estado local e UI.
 */

import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { Send, X, MessageCircle, Minimize2 } from 'lucide-react';
import { MensagemChat, MensagemChatInterface, AutorMensagem } from './MensagemChat';
import { criarChatSocket, type ChatMensagemEvento } from '../../servicos/servicoChatSocket';
import { useAutenticacao } from '../../../../core/contextos/ContextoAutenticacao';

// ===== TIPOS =====

/**
 * Modo de exibição do chat
 * - minimizado: Apenas botão flutuante visível
 * - aberto: Painel completo do chat visível
 */
type ModoExibicaoChat = 'minimizado' | 'aberto';

/**
 * Props do componente ComponenteChatIA
 */
interface ComponenteChatIAProps {
  /** ID do processo/petição (contexto da conversa) */
  processoId: string;

  /** Classe CSS adicional para customização (opcional) */
  className?: string;

  /** Modo de exibição inicial (padrão: minimizado) */
  modoInicial?: ModoExibicaoChat;

  /** Callback chamado quando uma mensagem é enviada (opcional, para debug) */
  aoEnviarMensagem?: (conteudo: string) => void;

  /** Callback chamado quando uma resposta da IA é recebida (opcional, para debug) */
  aoReceberResposta?: (conteudo: string) => void;
}

// ===== COMPONENTE =====

/**
 * Componente principal do chat com IA
 *
 * FLUXO DE USO:
 * 1. Usuário clica no botão flutuante (se minimizado)
 * 2. Chat abre em painel lateral
 * 3. Usuário digita mensagem no campo de input
 * 4. Pressiona Enter ou clica em "Enviar"
 * 5. Mensagem aparece imediatamente no histórico
 * 6. Requisição é enviada via WebSocket (TAREFA-092)
 * 7. Resposta da IA é recebida e exibida gradualmente
 *
 * @param props - Propriedades do componente
 * @returns Elemento JSX do chat
 */
export function ComponenteChatIA({
  processoId,
  className = '',
  modoInicial = 'minimizado',
  aoEnviarMensagem,
  aoReceberResposta,
}: ComponenteChatIAProps): JSX.Element {
  const { token } = useAutenticacao();
  
  // ===== ESTADO =====

  /**
   * Modo de exibição atual do chat (minimizado ou aberto)
   */
  const [modoExibicao, setModoExibicao] = useState<ModoExibicaoChat>(modoInicial);

  /**
   * Lista de mensagens da conversa (histórico completo)
   */
  const [mensagens, setMensagens] = useState<MensagemChatInterface[]>([]);

  /**
   * Conteúdo atual do campo de input (texto sendo digitado)
   */
  const [textoInput, setTextoInput] = useState<string>('');

  /**
   * Indica se há uma requisição em andamento (aguardando resposta da IA)
   */
  const [aguardandoResposta, setAguardandoResposta] = useState<boolean>(false);

  // ===== REFS =====

  /**
   * Referência ao container de mensagens para controle de scroll
   */
  const containerMensagensRef = useRef<HTMLDivElement>(null);

  /**
   * Referência ao textarea de input para controle de foco
   */
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const conexaoSocketRef = useRef<ReturnType<typeof criarChatSocket> | null>(null);
  const mensagemLoadingAtualRef = useRef<string | null>(null);

  // ===== EFEITOS =====

  /**
   * Rolar automaticamente para a última mensagem quando o histórico mudar
   */
  useEffect(() => {
    if (containerMensagensRef.current) {
      containerMensagensRef.current.scrollTop = containerMensagensRef.current.scrollHeight;
    }
  }, [mensagens]);

  /**
   * Focar no input quando o chat for aberto
   */
  useEffect(() => {
    if (modoExibicao === 'aberto' && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [modoExibicao]);

  /**
   * Conectar ao WebSocket do processo ao montar o componente
   */
  useEffect(() => {
    if (!processoId) return;

    // Evitar reconexão desnecessária
    if (conexaoSocketRef.current) {
      conexaoSocketRef.current.fechar();
      conexaoSocketRef.current = null;
    }

    conexaoSocketRef.current = criarChatSocket(processoId, {
      onOpen: () => {
        // Adicionar mensagem de boas-vindas se não houver mensagens
        setMensagens((prev) => {
          if (prev.length === 0) {
            return [{
              id: `msg-ia-welcome-${Date.now()}`,
              conteudo: "Olá! Você pode usar este chat para tirar dúvidas sobre o processo atual que está aberto.",
              autor: 'ia',
              timestamp: new Date().toISOString(),
              carregando: false,
            }];
          }
          return prev;
        });
      },
      onMensagem: (evento: ChatMensagemEvento) => {
        const conteudo = (evento?.conteudo || '').toString();
        const autor = (evento?.autor as AutorMensagem) || 'ia';
        const timestamp = (evento?.timestamp as string) || new Date().toISOString();

        // Ignorar mensagens vazias para evitar bug de mensagem inicial vazia
        if (!conteudo.trim()) return;

        // Se existe uma mensagem de loading aguardando e o autor é IA,
        // substituí-la pela resposta recebida
        if (autor === 'ia' && mensagemLoadingAtualRef.current) {
          const idLoading = mensagemLoadingAtualRef.current;
          mensagemLoadingAtualRef.current = null;
          setMensagens((prev) =>
            prev.map((msg) =>
              msg.id === idLoading
                ? { ...msg, id: `msg-ia-${Date.now()}`, conteudo, carregando: false, timestamp }
                : msg
            )
          );
          aoReceberResposta?.(conteudo);
          setAguardandoResposta(false);
          return;
        }

        // Caso contrário, só adicionar a mensagem ao histórico
        const novaMsg: MensagemChatInterface = {
          id: `msg-${autor}-${Date.now()}`,
          conteudo,
          autor,
          timestamp,
          carregando: false,
        };
        setMensagens((prev) => [...prev, novaMsg]);
        if (autor === 'ia') aoReceberResposta?.(conteudo);
      },
      onError: () => {
        // opcional: exibir estado de erro de conexão
      },
      onClose: () => {
        // opcional: exibir aviso de reconexão
      },
    }, token || undefined);

    return () => {
      try {
        conexaoSocketRef.current?.fechar();
      } catch {
        // ignore
      }
      conexaoSocketRef.current = null;
    };
  }, [processoId, aoReceberResposta, token]);

  // ===== HANDLERS =====

  /**
   * Alternar entre modo minimizado e aberto
   */
  const alternarModoExibicao = (): void => {
    setModoExibicao((prev) => (prev === 'minimizado' ? 'aberto' : 'minimizado'));
  };

  /**
   * Enviar mensagem do usuário
   *
   * FLUXO:
   * 1. Validar que o input não está vazio
   * 2. Criar objeto de mensagem do usuário
   * 3. Adicionar ao histórico local
   * 4. Limpar campo de input
   * 5. Enviar para backend via WebSocket (TAREFA-092)
   * 6. Criar mensagem "carregando" da IA
   * 7. Quando resposta chegar, substituir mensagem de carregando
   */
  const enviarMensagem = (): void => {
    // Validação: input não pode estar vazio
    const textoTrimmed = textoInput.trim();
    if (!textoTrimmed || aguardandoResposta) {
      return;
    }

    // Criar mensagem do usuário
    const novaMensagemUsuario: MensagemChatInterface = {
      id: `msg-usuario-${Date.now()}`,
      conteudo: textoTrimmed,
      autor: 'usuario' as AutorMensagem,
      timestamp: new Date().toISOString(),
      carregando: false,
    };

    // Adicionar mensagem do usuário ao histórico
    setMensagens((prev) => [...prev, novaMensagemUsuario]);

    // Limpar input
    setTextoInput('');

    // Callback opcional (para debug/logging)
    if (aoEnviarMensagem) {
      aoEnviarMensagem(textoTrimmed);
    }

    // Marcar como aguardando resposta
    setAguardandoResposta(true);

    // Enviar via WebSocket
    conexaoSocketRef.current?.enviarMensagem(textoTrimmed, {
      processo_id: processoId,
      origem: 'frontend',
    });

    // Adicionar uma mensagem da IA em modo carregando até chegar a resposta
    const msgLoading: MensagemChatInterface = {
      id: `msg-ia-loading-${Date.now()}`,
      conteudo: '',
      autor: 'ia',
      timestamp: new Date().toISOString(),
      carregando: true,
    };
    mensagemLoadingAtualRef.current = msgLoading.id;
    setMensagens((prev) => [...prev, msgLoading]);
  };

  /**
   * Lidar com tecla Enter no textarea
   * - Enter: Envia mensagem
   * - Shift+Enter: Quebra de linha
   */
  const lidarComTeclaEnter = (evento: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (evento.key === 'Enter' && !evento.shiftKey) {
      evento.preventDefault();
      enviarMensagem();
    }
  };

  /**
   * Limpar histórico de mensagens (reset)
   */
  const limparHistorico = (): void => {
    setMensagens([]);
  };

  // ===== RENDERIZAÇÃO =====

  // Renderizar botão flutuante quando minimizado
  if (modoExibicao === 'minimizado') {
    return (
      <div className={`fixed bottom-6 right-6 z-50 ${className}`}>
        <button
          onClick={alternarModoExibicao}
          className="rounded-full bg-blue-600 p-4 text-white shadow-lg transition-all duration-200 hover:scale-110 hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300"
          aria-label="Abrir chat com IA"
          title="Conversar com a IA"
        >
          <MessageCircle className="size-6" />
        </button>
      </div>
    );
  }

  // Renderizar painel completo do chat quando aberto
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex flex-col rounded-lg bg-white shadow-2xl ${className}`}
      style={{ width: '400px', height: '600px' }}
    >
      {/* Cabeçalho do chat */}
      <div className="flex items-center justify-between rounded-t-lg bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-white">
        <div className="flex items-center space-x-2">
          <MessageCircle className="size-5" />
          <h3 className="text-sm font-semibold">Assistente Jurídico IA</h3>
        </div>
        <div className="flex items-center space-x-2">
          {/* Botão de limpar histórico */}
          {mensagens.length > 0 && (
            <button
              onClick={limparHistorico}
              className="text-white transition-colors hover:text-gray-200"
              aria-label="Limpar histórico"
              title="Limpar conversa"
            >
              <X className="size-4" />
            </button>
          )}
          {/* Botão de minimizar */}
          <button
            onClick={alternarModoExibicao}
            className="text-white transition-colors hover:text-gray-200"
            aria-label="Minimizar chat"
            title="Minimizar"
          >
            <Minimize2 className="size-4" />
          </button>
        </div>
      </div>

      {/* Área de mensagens */}
      <div
        ref={containerMensagensRef}
        className="flex-1 space-y-2 overflow-y-auto bg-gray-50 p-4"
      >
        {/* Mensagem inicial de boas-vindas (se não houver mensagens) */}
        {mensagens.length === 0 && (
          <div className="flex h-full items-center justify-center">
            <div className="px-4 text-center text-gray-500">
              <MessageCircle className="mx-auto mb-3 size-12 text-gray-400" />
              <p className="mb-1 text-sm font-medium">
                Olá! Sou seu assistente jurídico.
              </p>
              <p className="text-xs">
                Faça perguntas sobre o processo, solicite ajustes na minuta ou
                peça esclarecimentos sobre a análise.
              </p>
            </div>
          </div>
        )}

        {/* Lista de mensagens */}
        {mensagens.map((mensagem) => (
          <MensagemChat key={mensagem.id} mensagem={mensagem} />
        ))}
      </div>

      {/* Área de input */}
      <div className="rounded-b-lg border-t border-gray-200 bg-white p-3">
        <div className="flex items-end space-x-2">
          {/* Textarea de input */}
          <textarea
            ref={textareaRef}
            value={textoInput}
            onChange={(e) => setTextoInput(e.target.value)}
            onKeyDown={lidarComTeclaEnter}
            placeholder="Digite sua mensagem... (Enter para enviar)"
            disabled={aguardandoResposta}
            className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
            rows={2}
            maxLength={2000}
          />

          {/* Botão de enviar */}
          <button
            onClick={enviarMensagem}
            disabled={!textoInput.trim() || aguardandoResposta}
            className="rounded-lg bg-blue-600 p-2.5 text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-300"
            aria-label="Enviar mensagem"
            title="Enviar"
          >
            <Send className="size-5" />
          </button>
        </div>

        {/* Contador de caracteres */}
        <div className="mt-1 flex items-center justify-between text-xs text-gray-400">
          <span>Shift+Enter para quebra de linha</span>
          <span>
            {textoInput.length}/2000
          </span>
        </div>
      </div>
    </div>
  );
}
